import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import AddIcon from '@mui/icons-material/Add'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import { SelectChangeEvent } from '@mui/material'
import {
  GridCellValue,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowsProp,
  GridSortModel,
} from '@mui/x-data-grid'

import Box from 'components/Common/Box'
import Button from 'components/Common/Button'
import Table from 'components/Common/Table'
import Tooltip from 'components/Common/Table/Tooltip'
import { TabObjects } from 'components/Common/Tabs'
import Title from 'components/Common/Title'
import { Filter } from 'components/Inspection/Dashboard/FilterCard'
import DateFilter from 'components/Inspection/Listing/DateFilter'
import FieldFilter from 'components/Inspection/Listing/FieldFilter'
import FilterByCriteria from 'components/Listing/FilterByCriteria'

import { cypressAddButton } from 'constants/cypress'
import { InspectionColumnField, pendingHash } from 'constants/inspection'
import { routes } from 'constants/routes'
import { PENDING_STATUS } from 'constants/status'
import {
  commonListColumns,
  defaultSortModel,
  selectItems,
} from 'constants/table'
import { textFiles } from 'constants/textFiles'
import { UrlParamNames } from 'constants/urlQuery'
import useLocale from 'hooks/useLocale'
import useNotification from 'hooks/useNotification'
import useQueryState from 'hooks/useQueryState'
import useTranslation from 'hooks/useTranslation'
import useUser from 'hooks/useUser'
import { CalendarRangeType } from 'models/date'
import { UserRoles } from 'models/role'
import {
  FilterInputVariable,
  GenericInputVariable,
  ListingFilterType,
} from 'models/services/base'
import {
  DeleteInspectionResponse,
  DeleteInspectionsResponse,
  ListInspection,
  ListStatus,
} from 'models/services/inspection/listing'
import { ExtendedStatus } from 'models/services/status'
import { InspectionStatusMap } from 'models/status'
import { getIsoDate } from 'utils/date'
import {
  checkSearchEmptiness,
  cleanFilters,
  generateFilterInput,
} from 'utils/filters'
import {
  createFieldSelectItems,
  createFieldSelectLabels,
  defaultFields,
} from 'utils/Inspection/listing'
import { buildDetailRoute } from 'utils/routes'
import {
  serializeFields,
  serializeFilters,
  serializePage,
  serializePageSize,
  serializeRange,
  serializeSearch,
  serializeSortModel,
} from 'utils/serializers'
import { verifyParam } from 'utils/verifyUrlData'

import {
  DELETE_INSPECTION,
  DELETE_INSPECTIONS,
} from 'graphQL/Inspection/Listing/mutations'
import {
  GET_INSPECTION_STATUSES,
  LIST_INSPECTIONS,
} from 'graphQL/Inspection/Listing/queries'

import { StyledTextField } from 'styles/inspection/listing'

const registeredInspectionTab = 'registered-inspections'

const InspectionListingPage = () => {
  const { show } = useNotification()
  const location = useLocation()
  const { validateAllowedRoles } = useUser()
  const isSupervisor = validateAllowedRoles([
    UserRoles.ADMIN,
    UserRoles.INSPECTOR_SUPERVISOR,
  ])
  const { search, hash } = location

  const defaultSortedFields = [...defaultFields].sort()

  const [tabValue, setTabValue] = useState<string | number>(
    location.hash === pendingHash ? PENDING_STATUS : registeredInspectionTab
  )

  const [selectedFields, setSelectedFields] = useQueryState<string[]>(
    UrlParamNames.FIELDS,
    (verifyParam(UrlParamNames.FIELDS, search) as string[]) ||
      defaultSortedFields,
    serializeFields,
    defaultSortedFields
  )

  const [filtersList, setFiltersList] = useQueryState<Filter[]>(
    UrlParamNames.FILTERS,
    (verifyParam(UrlParamNames.FILTERS, search) as Filter[]) || [],
    serializeFilters
  )

  const [dateRange, setDateRange] = useQueryState<CalendarRangeType>(
    UrlParamNames.DATE,
    (verifyParam(UrlParamNames.DATE, search) as CalendarRangeType) || [],
    serializeRange
  )

  const [searchValue, setSearchValue] = useQueryState<string | undefined>(
    UrlParamNames.SEARCH,
    verifyParam(UrlParamNames.SEARCH, search) as string,
    serializeSearch
  )

  const [pageSize, setPageSize] = useQueryState<number>(
    UrlParamNames.LIMIT,
    (verifyParam(UrlParamNames.LIMIT, search) as number) || 10,
    serializePageSize
  )
  const [page, setPage] = useQueryState<number>(
    UrlParamNames.PAGE,
    (verifyParam(UrlParamNames.PAGE, search) as number) || 1,
    serializePage
  )

  const [sortModel, setSortModel] = useQueryState<GridSortModel>(
    UrlParamNames.SORT,
    (verifyParam(UrlParamNames.SORT, search) as GridSortModel) ||
      defaultSortModel,
    serializeSortModel,
    defaultSortModel
  )
  const [filterInput, setFiltersInput] = useState<ListingFilterType>(
    generateFilterInput(filtersList)
  )
  const [searchInput, setSearchInput] = useState<string>(searchValue || '')

  const [selectedLanguage] = useLocale()
  const history = useHistory()

  const [deleteItem, setDeleteItem] = useState<GridRowId | null>(null)
  const { text } = useTranslation(textFiles.INSPECTION_LISTING)
  const { text: generalText } = useTranslation(textFiles.GENERAL)
  const [data, setData] = useState<GridRowsProp>([])
  const [inspectionCount, setInspectionCount] = useState<number>(0)

  const [status, setStatus] = useState<ExtendedStatus[]>([])

  const fieldSelectItems = createFieldSelectItems(text.fieldSelect)
  const fieldSelectLabels = createFieldSelectLabels(text.fieldSelect)

  const [showOrigin, setShowOrigin] = useState<boolean>(true)

  const { fromDate, toDate } = useMemo(() => {
    return {
      fromDate: getIsoDate(dateRange.fromDate, 'date'),
      toDate: getIsoDate(dateRange.toDate, 'date'),
    }
  }, [dateRange])

  const {
    data: inspectionData,
    loading: inspectionLoading,
    refetch: refetchInspection,
  } = useQuery<ListInspection, FilterInputVariable>(LIST_INSPECTIONS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        limit: Number(pageSize),
        start: (page - 1) * Number(pageSize),
        where: {
          text_search: searchValue,
          date_gte: fromDate,
          date_lte: toDate,
          ...(tabValue === PENDING_STATUS
            ? { status_eq: PENDING_STATUS }
            : { status_ne: PENDING_STATUS }),
          ...cleanFilters(filterInput),
        },
        sort:
          sortModel.length > 0 && sortModel[0].sort
            ? {
                [sortModel[0].field]: sortModel[0].sort,
              }
            : undefined,
      },
    },
    onError() {
      show({
        updatedSeverity: 'error',
      })
    },
  })

  const { data: statusData, loading: statusLoading } = useQuery<
    ListStatus,
    FilterInputVariable
  >(GET_INSPECTION_STATUSES, {
    variables: {
      input: {
        sort: {
          slug: 'asc',
        },
      },
    },
  })

  const handleShowError = () => {
    show({
      updatedSeverity: 'error',
      message: generalText.notificationText.deleteFail,
    })
  }

  const handleRefetchInspections = async () => {
    try {
      const response = await refetchInspection()

      if (response) {
        const inspections = response.data.listInspections
        if (inspections.data.length === 0 && inspections.count > 0) {
          setPage(1)
        }
      }
      show({
        updatedSeverity: 'success',
        message: generalText.notificationText.deleteSuccess,
      })
    } catch {
      handleShowError()
    }
  }

  const [removeInspection] = useMutation<
    DeleteInspectionResponse,
    GenericInputVariable<string>
  >(DELETE_INSPECTION, {
    onCompleted() {
      handleRefetchInspections()
    },
    onError() {
      handleShowError()
    },
  })

  const [removeInspections] = useMutation<
    DeleteInspectionsResponse,
    GenericInputVariable<string[]>
  >(DELETE_INSPECTIONS, {
    onCompleted() {
      handleRefetchInspections()
    },
    onError() {
      handleShowError()
    },
  })

  const deleteInspection = React.useCallback((id: GridRowId) => {
    setDeleteItem(id)
  }, [])

  const editInspection = React.useCallback(
    (id: GridCellValue) => {
      history.push(buildDetailRoute(id, routes.INSPECTION_DETAIL))
    },
    [history]
  )

  const createInspection = () => {
    history.push(routes.INSPECTION_CREATION)
  }

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model)
  }

  const handleRemoveInspection = async (id: string) => {
    removeInspection({
      variables: {
        input: id,
      },
    })
  }

  const handleRemoveMultipleInspections = async (ids: string[]) => {
    removeInspections({
      variables: {
        input: ids,
      },
    })
  }

  const createColumns = useCallback(
    (statuses?: ExtendedStatus[]) => {
      const {
        id: idColumn,
        statusBackend: statusColumn,
        standard: standardColumn,
        actions: actionsColumn,
        provider: providerColumn,
        isoDate: dateColumn,
      } = commonListColumns(
        InspectionStatusMap,
        selectedLanguage,
        generalText,
        editInspection,
        isSupervisor ? deleteInspection : undefined,
        statuses
      )

      return [
        { ...idColumn, hide: !selectedFields.includes(idColumn.field) },
        {
          ...dateColumn,
          headerName: text.table.date,
          hide: !selectedFields.includes(dateColumn.field),
        },
        {
          ...providerColumn,
          hide: !selectedFields.includes(providerColumn.field),
        },
        {
          ...standardColumn,
          field: InspectionColumnField.PHONE,
          headerName: text.table.providerTelephoneNumber,
          hide: !selectedFields.includes(InspectionColumnField.PHONE),
          flex: 2,
          renderCell: (params: GridRenderCellParams) => {
            if (!params.row.providerTelephoneNumber)
              return generalText.table.notRegistered

            return <Tooltip>{params.row.providerTelephoneNumber}</Tooltip>
          },
        },
        {
          ...dateColumn,
          field: InspectionColumnField.CREATED_AT,
          hide: !selectedFields.includes(InspectionColumnField.CREATED_AT),
          headerName: text.table.createdAt,
        },
        {
          ...statusColumn,
          hide: !selectedFields.includes(statusColumn.field),
          sortable: tabValue !== PENDING_STATUS,
        },
        {
          ...standardColumn,
          field: InspectionColumnField.CAR_NAME,
          headerName: text.table.carName,
          hide: !selectedFields.includes(InspectionColumnField.CAR_NAME),
          sortable: false,
          flex: 2,
          renderCell: (params: GridRenderCellParams) => {
            const { year, model } = params.row.car
            const title = `${year} ${model.brand.name} ${model.name}`
            return <Tooltip>{title}</Tooltip>
          },
        },
        {
          ...actionsColumn,
          hide: !selectedFields.includes(actionsColumn.field),
        },
      ]
    },
    [
      selectedLanguage,
      generalText,
      editInspection,
      deleteInspection,
      selectedFields,
      text.table.date,
      text.table.providerTelephoneNumber,
      text.table.createdAt,
      text.table.carName,
      tabValue,
      isSupervisor,
    ]
  )

  const [columns, setColumns] = useState<GridColDef[]>(createColumns())

  const tabs: TabObjects[] = [
    {
      id: 1,
      item: {
        label: text.registeredTab,
        value: registeredInspectionTab,
      },
    },
    {
      id: 2,
      item: {
        label: text.incomingTab,
        value: PENDING_STATUS,
      },
    },
  ]

  const handleFiltersList = (
    newFiltersList: Filter[],
    newFilterInput: ListingFilterType
  ) => {
    setFiltersList(newFiltersList)
    setFiltersInput(newFilterInput)
    setPage(1)
  }

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setSearchInput(event.target.value)
  }

  const submitSearchValue = () => {
    setPage(1)
    const newValue = checkSearchEmptiness(searchInput)
    setSearchValue(newValue)
  }

  const handleTabChange = (newValue: string | number) => {
    if (newValue === PENDING_STATUS)
      history.replace({ ...location, hash: pendingHash })
    else history.replace({ ...location, hash: '#' })
    setPage(1)
  }

  const handleSelectItem = (valueInput: string) => {
    setColumns((prevColumns) =>
      prevColumns.map((column) => {
        return column.field === valueInput
          ? { ...column, hide: !column.hide }
          : column
      })
    )
    setSelectedFields((prevFields) => {
      if (prevFields.includes(valueInput)) {
        if (valueInput === InspectionColumnField.SOURCE) setShowOrigin(false)
        return prevFields.filter((field) => field !== valueInput)
      }
      if (valueInput === InspectionColumnField.SOURCE) setShowOrigin(true)
      return [...prevFields, valueInput]
    })
  }

  const handleResetDefault = () => {
    setSelectedFields(defaultFields)
    setColumns((prevColumns) =>
      prevColumns.map((column) => {
        const fieldExist = defaultFields.some(
          (defaultField) => column.field === defaultField
        )
        return { ...column, hide: !fieldExist }
      })
    )
    setShowOrigin(true)
  }

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage)
  }

  const handleChangePageSize = (event: SelectChangeEvent<number>) => {
    setPageSize(event.target.value as number)
  }

  const handleChangeDateRange = (newDateRange: CalendarRangeType) => {
    setDateRange(newDateRange)
    setPage(1)
  }

  const getPageCount = () => {
    return Math.ceil(inspectionCount / pageSize)
  }

  useEffect(() => {
    if (inspectionData) {
      const { count, data: inspData } = inspectionData.listInspections
      setData(
        inspData.map((inspection) => {
          return { ...inspection, showOrigin }
        })
      )
      setInspectionCount(count)
    }
  }, [inspectionData, showOrigin])

  useEffect(() => {
    if (statusData) {
      const formattedStatuses = statusData.getInspectionStatuses.map(
        (currentStatus) => ({
          ...currentStatus,
          slug: currentStatus.slug.toLowerCase().replace('_', '-'),
        })
      )
      setColumns(createColumns(formattedStatuses))
      setStatus(formattedStatuses)
    }
  }, [statusData, createColumns])

  useEffect(() => {
    setColumns((prevColumns) =>
      prevColumns.map((column) => {
        const fieldExist = selectedFields.some(
          (defaultField) => column.field === defaultField
        )
        return { ...column, hide: !fieldExist }
      })
    )
  }, [createColumns, selectedFields])

  useEffect(() => {
    if (hash && hash === pendingHash) {
      setTabValue(PENDING_STATUS)
    } else {
      setTabValue(registeredInspectionTab)
    }
  }, [hash])

  return (
    <Box width="100%">
      <Title
        icon={<PlaylistAddCheckIcon />}
        handleTabChange={handleTabChange}
        subtitle={`${inspectionCount} ${text.description}`}
        tabs={tabs}
        tabValue={tabValue}
        title={text.title}
      />
      <Box
        alignItems="center"
        display="flex"
        justifyContent={
          tabValue === registeredInspectionTab ? 'space-between' : 'flex-end'
        }
        marginTop="2rem"
        width="100%"
      >
        {tabValue === registeredInspectionTab ? (
          <Button
            startIcon={<AddIcon />}
            size="medium"
            onClick={createInspection}
            style={{ minWidth: '160px', marginRight: '20px' }}
            data-cy={cypressAddButton}
          >
            {text.newInspectionButton}
          </Button>
        ) : null}

        <Box display="flex" flex="1 0.8 auto" justifyContent="flex-end">
          <Box marginRight="1rem" flex="0 1 430px">
            <StyledTextField
              placeholder={text.searchPlaceholder}
              fullWidth
              value={searchInput}
              onChange={handleSearchChange}
              submitFunction={submitSearchValue}
              name="search"
            />
          </Box>
          <Box marginRight="1rem">
            <FilterByCriteria
              filtersList={filtersList}
              handleFiltersList={handleFiltersList}
              file={textFiles.INSPECTION_LISTING}
              filterInput={filterInput}
              statusList={status}
            />
          </Box>
          <Box marginRight="1rem">
            <FieldFilter
              handleSelectItem={handleSelectItem}
              items={fieldSelectItems}
              selectedValues={selectedFields}
              handleResetDefault={handleResetDefault}
            />
          </Box>
          <Box>
            <DateFilter
              dateRange={dateRange}
              handleChangeDateRange={handleChangeDateRange}
              title={text.dateRangeTitle}
              maxDate={null}
            />
          </Box>
        </Box>
      </Box>
      <Table
        columns={columns}
        dateRange={dateRange}
        searchValue={searchValue}
        data={data}
        currentPage={page}
        onPageChange={handleChangePage}
        onSelectChange={handleChangePageSize}
        pageSize={Number(pageSize)}
        selectItems={selectItems}
        pageCount={getPageCount()}
        filtersList={filtersList}
        fields={selectedFields}
        fieldLabels={fieldSelectLabels}
        deletedItemId={deleteItem}
        setDeleteItemId={setDeleteItem}
        route={routes.INSPECTION_DETAIL}
        sortModel={sortModel}
        handleSortModelChange={handleSortModelChange}
        loading={inspectionLoading || statusLoading}
        handleRemoveItem={handleRemoveInspection}
        handleRemoveMultipleItems={handleRemoveMultipleInspections}
        hideDownloadModal
        checkboxSelection={isSupervisor}
      />
    </Box>
  )
}
export default InspectionListingPage
